import { Link } from "react-router-dom";
import styled, { keyframes, useTheme } from "styled-components";
import { BookOpen, Headphones, Notebook, SpeakerHigh, type Icon } from "@phosphor-icons/react";

type Feature = { href: string; title: FeatureKey; icon: Icon; gradient: [string, string] };
type FeatureKey = "surah" | "listen" | "mafatih" | "duas";

const FEATURES: Feature[] = [
  { href: "/surah",  title: "surah",  icon: BookOpen,   gradient: ["#3b82f6", "#60a5fa"] },
  { href: "/listen", title: "listen", icon: Headphones, gradient: ["#22c55e", "#4ade80"] },
  { href: "/mafatih",title: "mafatih",icon: Notebook,   gradient: ["#f97316", "#fb923c"] },
  { href: "/duas",   title: "duas",   icon: SpeakerHigh,gradient: ["#a855f7", "#c084fc"] },
];

const TEXTS: Record<"ar" | "en", Record<FeatureKey, string>> = {
  ar: { surah: "المصحف الشريف", listen: "الاستماع للقرآن", mafatih: "مفاتيح الجنان", duas: "الاستماع للأدعية" },
  en: { surah: "Holy Quran",     listen: "Listen to Quran", mafatih: "Mafatih al-Jinan", duas: "Listen to Duas" },
};

export default function FeatureCards() {
  const { dir } = useTheme() as { dir: "rtl" | "ltr" };
  const lang = dir === "rtl" ? "ar" : "en";

  return (
    <Grid role="list">
      {FEATURES.map(({ href, title, icon: Icon, gradient }) => (
        <Card role="listitem" key={href} to={href} aria-label={title} $grad={gradient} tabIndex={0}>
          <Gradient />
          <Glass aria-hidden />
          <IconChip aria-hidden>
            <ChipShade />
            <Icon size={36} color="#fff" weight="bold" />
          </IconChip>
          <Title>{TEXTS[lang][title]}</Title>
        </Card>
      ))}
    </Grid>
  );
}

/* layout */
const Grid = styled.div`
  display: grid; gap: 20px; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); margin-bottom: clamp(32px, 5vw, 56px);
  @media (max-width: 600px) { grid-template-columns: 1fr 1fr; gap: 14px; }
`;
const pop = keyframes` from { transform: translateY(6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } `;
const Card = styled(Link)<{ $grad: [string, string] }>`
  position: relative; isolation:isolate; gap:20px;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  text-align:center; aspect-ratio:1/1; border-radius:20px; overflow:hidden;
  color:#fff; text-decoration:none; 
  box-shadow: 0 4px 16px rgba(0,0,0,.1), 0 2px 4px rgba(0,0,0,.06);
  transition: transform 200ms cubic-bezier(0.4, 0, 0.2, 1), box-shadow 200ms ease;
  animation: ${pop} 320ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
  cursor:pointer; 
  &:hover{ 
    transform: translateY(-6px) scale(1.02); 
    box-shadow: 0 8px 24px rgba(0,0,0,.15), 0 4px 8px rgba(0,0,0,.08);
  } 
  &:active{ transform: translateY(-2px) scale(1.01); }
  --g0:${({ $grad }) => $grad[0]}; --g1:${({ $grad }) => $grad[1]};
`;
const Gradient = styled.div` position:absolute; inset:0; background:linear-gradient(135deg, var(--g0), var(--g1)); z-index:0; `;
const Glass = styled.div`
  position:absolute; inset:0; border-radius:inherit; background:rgba(0,0,0,.34); border:1px solid rgba(255,255,255,.14); z-index:1;
  html[data-theme="dark"] & { background:rgba(17,17,17,.7); border-color:rgba(255,255,255,.18); }
`;
const IconChip = styled.div`
  position:relative; z-index:2; width:80px; height:80px; border-radius:22px; display:grid; place-items:center; margin-bottom:8px;
  overflow:hidden; border:1.5px solid rgba(255,255,255,.25);
  box-shadow: 0 4px 12px rgba(0,0,0,.15);
  @media (max-width:600px){ width:68px; height:68px; border-radius:18px; margin-bottom:4px; }
`;
const Title = styled.span` 
  position:relative; z-index:2; font-weight:500; letter-spacing:.3px; line-height:1.3; 
  font-size:clamp(1rem, 2vw, 1.15rem); 
  text-shadow: 0 2px 4px rgba(0,0,0,.2);
`;
const ChipShade = styled.div` position:absolute; inset:0; background:linear-gradient(180deg, rgba(0,0,0,.18) 0%, rgba(0,0,0,.05) 100%); `;
